// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState } from 'react'
import { Check, AlertTriangle, Zap, Wrench } from 'lucide-react'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '20px', fontFamily: 'Georgia, serif',
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)',
    border: `1px solid ${C.border}`,
  },
  header: {
    padding: '24px 28px 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '22px',
    cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1,
    flexShrink: 0, marginLeft: '12px',
  },
  body: { padding: '16px 28px 28px' },
  taskTitle: { fontSize: '20px', fontWeight: '700', color: C.brown, marginBottom: '4px' },
  applianceName: { fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' },
  metaRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' },
  metaBadge: (color) => ({
    fontSize: '11px', padding: '4px 12px', borderRadius: '20px',
    fontFamily: 'Calibri, sans-serif', fontWeight: '600',
    background: color.bg, color: color.text, border: `1px solid ${color.text}33`,
  }),
  divider: { borderTop: `1px solid ${C.border}`, margin: '16px 0' },
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '10px',
    fontFamily: 'Calibri, sans-serif',
  },
  toolsList: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' },
  toolChip: {
    fontSize: '12px', padding: '4px 12px', borderRadius: '20px',
    background: C.bg, border: `1px solid ${C.border}`,
    color: C.brown, fontFamily: 'Calibri, sans-serif',
  },
  stepRow: { display: 'flex', gap: '14px', marginBottom: '16px', alignItems: 'flex-start' },
  stepNumber: {
    width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '700', fontFamily: 'Calibri, sans-serif',
  },
  stepText: {
    fontSize: '14px', color: C.brown, lineHeight: 1.7,
    fontFamily: 'Calibri, sans-serif', paddingTop: '4px',
  },
  proTip: {
    background: C.warningBg, border: `1px solid ${C.amber}44`,
    borderRadius: '10px', padding: '14px 16px', marginTop: '4px',
  },
  proTipLabel: {
    fontSize: '10px', color: C.amber, letterSpacing: '1.5px',
    textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif',
    fontWeight: '700', marginBottom: '4px',
  },
  proTipText: { fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 },
  warningBox: {
    background: '#FDECEA', border: `1px solid ${C.danger}44`,
    borderRadius: '10px', padding: '14px 16px', marginBottom: '16px',
  },
  warningText: { fontSize: '13px', color: C.danger, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 },
  contractorNote: {
    background: C.successBg, border: `1px solid ${C.success}44`,
    borderRadius: '10px', padding: '14px 16px', marginBottom: '16px',
  },
  contractorNoteText: { fontSize: '13px', color: C.success, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 },
  secondaryBtn: {
    width: '100%', background: 'transparent',
    border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px',
    color: C.muted, fontSize: '13px', letterSpacing: '1px',
    textTransform: 'uppercase', cursor: 'pointer',
    fontFamily: 'Georgia, serif', marginTop: '20px',
  },
}

const difficultyColors = {
  'Easy':     { bg: '#EBF5EF', text: '#2E6B4A' },
  'Moderate': { bg: '#FBF3E8', text: '#B8721F' },
  'Advanced': { bg: '#FDECEA', text: '#B03020' },
}

const typeColors = {
  diy:        { bg: '#F2EDE4', text: '#9A7B68' },
  contractor: { bg: '#EBF5EF', text: '#2E6B4A' },
  auto_order: { bg: '#FBF3E8', text: '#B8721F' },
}

const typeLabels = {
  diy: 'DIY',
  contractor: 'Contractor Task',
  auto_order: 'Order Parts',
}

export const guides = {

  // ── HVAC ──────────────────────────────────────────────────────────────────

  hvac_filter_replacement: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['New filter (check size on old filter)', 'Marker or pen'],
    steps: [
      'Turn off your HVAC system at the thermostat before you begin.',
      'Locate the filter — it\'s usually in the return air vent (a large grate on the wall or ceiling) or inside the air handler unit itself.',
      'Open the vent cover or access panel. Most covers have tabs or screws.',
      'Slide the old filter out. Note the direction of the arrows on the frame — these indicate airflow direction.',
      'Check the size printed on the old filter frame (e.g. 20x25x1) and confirm your new filter matches.',
      'Slide the new filter in with the arrows pointing toward the air handler.',
      'Close the cover, then write the installation date on the filter frame with a marker.',
      'Turn your system back on at the thermostat.',
    ],
    proTip: 'A clogged filter is the #1 cause of HVAC failure. Set a phone reminder every 90 days — it takes less time than making coffee and can save you thousands in repairs.',
    type: 'auto_order',
  },

  hvac_coil_cleaning: {
    difficulty: 'Advanced', time: '2–4 hours',
    tools: ['Fin comb', 'Coil cleaner spray', 'Garden hose', 'Screwdriver', 'Work gloves', 'Safety glasses'],
    contractorNote: 'Most homeowners hire this out. The outdoor condenser coils can be rinsed yourself, but indoor evaporator coils require opening the air handler — a mistake can be costly.',
    steps: [
      'Turn off power to the outdoor unit at the disconnect box near the unit.',
      'Remove any debris — leaves, grass, sticks — from around and on top of the unit.',
      'Unscrew the top grille. Carefully lift it off — the fan may still be attached by wires.',
      'Use a garden hose (not a pressure washer) to spray water from the inside out through the fins.',
      'For heavier buildup, spray coil cleaner on the fins, let it foam for 5 minutes, then rinse.',
      'If any fins are bent, use a fin comb to gently straighten them.',
      'Replace the grille and screws.',
      'Restore power and wait 30 minutes before running the system.',
    ],
    proTip: 'Do this every spring before cooling season. A clean condenser can reduce your electricity bill by up to 15% and extend the life of your compressor significantly.',
    type: 'contractor',
  },

  hvac_condensate_drain_flush: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['White vinegar or condensate drain tablets', 'Wet/dry vacuum (optional)'],
    steps: [
      'Turn off your HVAC system at the thermostat.',
      'Locate the condensate drain line — a white PVC pipe that exits your air handler.',
      'Find the access point — usually a T-shaped cap near the air handler. Remove it.',
      'Pour 1/4 cup of plain white distilled vinegar into the drain.',
      'Wait 30 minutes, then flush with a cup of water.',
      'If the drain is already slow, attach a wet/dry vacuum to the outdoor end and run it for 1–2 minutes.',
      'Replace the cap and turn your system back on.',
    ],
    proTip: 'A clogged condensate drain is one of the most common causes of water damage from HVAC systems. In humid climates, do this every 3 months.',
    type: 'diy',
  },

  hvac_full_system_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'This is a professional service. A certified HVAC technician will check refrigerant levels, electrical connections, blower motor, heat exchanger, and overall system efficiency.',
    steps: [
      'Schedule with a licensed HVAC contractor in September or October before heating season begins.',
      'Before they arrive, clear access to your air handler, outdoor unit, and all vents.',
      'Ask the technician to check: refrigerant charge, blower motor, heat exchanger for cracks, thermostat calibration, all electrical connections, and duct leakage.',
      'Ask for a written report of findings and any recommended repairs.',
      'If your system is over 12 years old, ask about efficiency ratings for newer systems.',
    ],
    proTip: 'Annual inspections typically cost $80–$150 and can catch a failing capacitor ($20 part) before it kills your compressor ($1,500+ repair).',
    type: 'contractor',
  },

  // ── REFRIGERATOR ──────────────────────────────────────────────────────────

  refrigerator_water_filter_replacement: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['Replacement filter (check your model number)'],
    steps: [
      'Find your filter location — most are inside the fridge in the upper right corner or in the base grille.',
      'For twist-style filters: turn counterclockwise and pull straight out.',
      'For push-button filters: press the eject button and pull the filter out.',
      'Unwrap the new filter and remove any protective caps.',
      'Insert the new filter and push in firmly, then twist clockwise until it locks.',
      'Run 2–3 gallons of water through the dispenser to flush air and carbon particles.',
      'Reset the filter indicator light — usually hold the "Filter Reset" button for 3 seconds.',
    ],
    proTip: 'Using an expired filter is worse than no filter — it can release trapped contaminants back into your water. Write the replacement date on the filter with a marker.',
    type: 'auto_order',
  },

  refrigerator_condenser_coil_cleaning: {
    difficulty: 'Easy', time: '20 minutes',
    tools: ['Vacuum with brush attachment', 'Coil cleaning brush (optional)', 'Flashlight'],
    steps: [
      'Unplug the refrigerator from the wall outlet.',
      'Pull the refrigerator away from the wall about 2 feet, or remove the base grille at the front bottom.',
      'Locate the condenser coils — they look like a black metal grid.',
      'Use the vacuum brush attachment to remove dust and debris from the coils.',
      'For heavy buildup, use a coil cleaning brush to loosen dust before vacuuming.',
      'Also vacuum the condenser fan and the area around it.',
      'Replace the grille or push the refrigerator back, then plug it back in.',
    ],
    proTip: 'Dusty condenser coils make your refrigerator work 30% harder. If your fridge feels warm on the outside or food isn\'t staying cold, dirty coils are often the culprit.',
    type: 'diy',
  },

  refrigerator_door_seal_inspection: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Dollar bill or piece of paper'],
    steps: [
      'Close the refrigerator door on a dollar bill so half is inside and half is outside.',
      'Try to pull the bill out — you should feel resistance. If it slides out easily, the seal is weak.',
      'Repeat this test around the entire door perimeter.',
      'Visually inspect the gasket for cracks, tears, mold, or areas pulling away from the door.',
      'Clean the gasket with warm soapy water and a toothbrush.',
      'If the gasket is cracked or deformed, search your model number plus "door gasket" to find the correct replacement part.',
    ],
    proTip: 'A bad door seal can increase your energy bill by $50–$100 per year. A new gasket typically costs $20–$60 and takes 15 minutes to swap.',
    type: 'diy',
  },

  // ── WATER HEATER ──────────────────────────────────────────────────────────

  water_heater_tank_flush: {
    difficulty: 'Moderate', time: '45 minutes',
    tools: ['Garden hose', 'Flathead screwdriver', 'Work gloves', 'Bucket (optional)'],
    warning: 'The water in your tank is very hot. Let the heater cool for at least 2 hours before starting, or work carefully with gloves.',
    steps: [
      'Turn the water heater to "Pilot" mode (gas) or flip the circuit breaker off (electric).',
      'Connect a garden hose to the drain valve at the bottom of the tank.',
      'Turn off the cold water supply valve at the top of the heater.',
      'Open a hot water faucet somewhere in your house to allow air into the system.',
      'Open the drain valve — water will begin flowing through the hose.',
      'Let the tank drain completely.',
      'Briefly turn the cold water supply back on to flush remaining sediment until water runs clear.',
      'Close the drain valve, disconnect the hose, then turn cold water supply back on to refill.',
      'Keep the hot water faucet open until water flows steadily, then close it.',
      'Turn the heater back on and allow 1–2 hours to fully reheat.',
    ],
    proTip: 'Sediment buildup reduces efficiency and causes a popping or rumbling noise. In hard water areas, flush every 6 months instead of annually.',
    type: 'diy',
  },

  'water_heater_tank_flush_&_sediment_drain': {
    difficulty: 'Moderate', time: '45 minutes',
    tools: ['Garden hose', 'Flathead screwdriver', 'Work gloves'],
    warning: 'The water in your tank is very hot. Let the heater cool for at least 2 hours before starting.',
    steps: [
      'Turn the water heater to "Pilot" mode (gas) or flip the circuit breaker off (electric).',
      'Connect a garden hose to the drain valve at the bottom of the tank.',
      'Turn off the cold water supply valve at the top of the heater.',
      'Open a hot water faucet somewhere in your house to allow air into the system.',
      'Open the drain valve — water will begin flowing through the hose.',
      'Let the tank drain completely.',
      'Briefly turn the cold water supply back on to flush remaining sediment until the water runs clear.',
      'Close the drain valve, disconnect the hose, then turn the cold water supply back on to refill.',
      'Keep the hot water faucet open until water flows steadily, then close it.',
      'Turn the heater back on and allow 1–2 hours to fully reheat.',
    ],
    proTip: 'Sediment buildup reduces efficiency and causes a popping or rumbling noise. In hard water areas, flush every 6 months.',
    type: 'diy',
  },

  water_heater_anode_rod_inspection: {
    difficulty: 'Advanced', time: '1 hour',
    tools: ['1-1/16 inch socket wrench', 'Breaker bar', 'Replacement anode rod', 'Teflon tape', 'Work gloves'],
    contractorNote: 'The anode rod is the sacrificial element that prevents your tank from rusting. It\'s doable as DIY if you\'re comfortable with basic plumbing, but many homeowners hire this out.',
    steps: [
      'Turn off the water heater and cold water supply. Let the tank cool for at least 2 hours.',
      'Drain about 10% of the tank via the drain valve to reduce pressure.',
      'Locate the anode rod — usually under a plastic cap on top of the water heater.',
      'Use a 1-1/16 inch socket with a breaker bar to loosen the rod.',
      'Once loose, unscrew by hand and pull the rod out.',
      'Inspect the rod: if it\'s less than 1/2 inch thick or the core wire is exposed, replace it.',
      'Wrap the threads of the new rod with Teflon tape, then thread it in and tighten.',
      'Turn the cold water supply back on and restore power or relight the pilot.',
    ],
    proTip: 'A working anode rod can double the life of your water heater. A new rod costs $20–$50. A new water heater costs $600–$1,500.',
    type: 'contractor',
  },

  water_heater_pressure_relief_valve_test: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Bucket', 'Work gloves'],
    warning: 'The pressure relief valve releases very hot water and steam. Keep your face and hands clear. Place a bucket under the discharge pipe before testing.',
    steps: [
      'Locate the pressure relief valve — on the side or top of the water heater with a lever and a pipe running down toward the floor.',
      'Place a bucket under the end of the discharge pipe.',
      'Lift the test lever briefly (1–2 seconds) and release it.',
      'You should hear a rush of hot water and steam. The lever should snap back and stop flowing.',
      'If water continues dripping after releasing, the valve is faulty and needs replacement.',
      'If no water came out, the valve may be stuck — have a plumber replace it.',
    ],
    proTip: 'A faulty pressure relief valve is a safety hazard. Replacement typically costs $50–$150 and takes about 30 minutes.',
    type: 'diy',
  },

  // ── WASHER ────────────────────────────────────────────────────────────────

  washer_drum_cleaning_cycle: {
    difficulty: 'Easy', time: '1 hour (machine runs itself)',
    tools: ['Washing machine cleaner tablet or 2 cups white vinegar + 1/2 cup baking soda'],
    steps: [
      'Make sure the drum is empty.',
      'If using a cleaner tablet: drop it directly into the drum.',
      'If using vinegar: pour 2 cups into the detergent dispenser. Add 1/2 cup baking soda into the drum.',
      'Select the hottest water setting and longest cycle, or use your machine\'s "Clean Washer" cycle.',
      'Run the full cycle.',
      'Wipe down the inside of the drum, door glass, and door gasket with a dry cloth.',
      'Leave the door open for a few hours to air out.',
    ],
    proTip: 'That musty smell from clothes? It\'s usually a dirty drum. Monthly cleaning eliminates it entirely.',
    type: 'diy',
  },

  washer_door_gasket_wipe_down: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['White vinegar', 'Spray bottle', 'Old toothbrush', 'Dry cloth'],
    steps: [
      'Mix equal parts white vinegar and water in a spray bottle.',
      'Pull back the rubber gasket around the door opening and look inside the folds.',
      'Spray the vinegar solution inside the gasket folds and let it sit for 2 minutes.',
      'Use an old toothbrush to scrub any mold or black residue.',
      'Wipe clean with a damp cloth, then dry thoroughly.',
      'Check the gasket for any tears or holes — a damaged gasket will cause leaks.',
    ],
    proTip: 'After every wash, leave the door slightly open and wipe the gasket dry. This one habit prevents nearly all front-loader mold issues.',
    type: 'diy',
  },

  washer_inlet_hose_inspection: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Flashlight'],
    steps: [
      'Pull the washer away from the wall enough to see the back.',
      'Locate the two hoses connected to the back — one hot, one cold.',
      'Inspect both hoses for: bulging, cracking, fraying, kinking, or corrosion at the fittings.',
      'Check the connections at both ends for moisture, rust, or mineral deposits.',
      'Try gently tightening the connection by hand.',
      'If you see any bulging or cracking, or the hose is more than 5 years old, replace it proactively.',
    ],
    proTip: 'A burst washing machine hose is one of the most common causes of catastrophic home water damage. A replacement braided hose costs about $15 and takes 5 minutes.',
    type: 'diy',
  },

  // ── DRYER ─────────────────────────────────────────────────────────────────

  dryer_lint_trap_cleaning: {
    difficulty: 'Easy', time: '2 minutes',
    tools: ['Your hand'],
    steps: [
      'Before or after every load, pull the lint screen out of its slot.',
      'Peel the lint off with your fingers and discard it.',
      'Slide the screen back in firmly.',
      'Once a month, wash the screen with warm soapy water and a soft brush to remove fabric softener residue.',
      'Let it dry completely before reinserting.',
    ],
    proTip: 'Lint buildup in the trap is the leading cause of dryer fires — nearly 3,000 per year in the US. Two minutes per load prevents it.',
    type: 'diy',
  },

  dryer_vent_duct_cleaning: {
    difficulty: 'Advanced', time: '1–2 hours',
    tools: ['Dryer vent cleaning brush kit', 'Vacuum with hose attachment', 'Screwdriver', 'Duct tape'],
    contractorNote: 'Many confident DIYers do this themselves. If your duct runs more than 10 feet or has multiple bends, a professional cleaning kit or service is recommended.',
    steps: [
      'Unplug the dryer and turn off the gas if it\'s a gas dryer.',
      'Pull the dryer away from the wall and disconnect the duct from the back.',
      'Go outside and locate the exterior vent cap. Remove any cover.',
      'Insert the dryer vent cleaning brush into the duct from outside, using rod extensions.',
      'Vacuum out loosened lint from both the exterior opening and interior duct end.',
      'Reattach the duct to the dryer securely.',
      'Push the dryer back, plug in, and run it on air-only for 10 minutes.',
    ],
    proTip: 'A clogged vent duct doubles drying time. If clothes take more than one cycle to dry, a clogged duct is the most likely cause.',
    type: 'contractor',
  },

  dryer_exterior_vent_check: {
    difficulty: 'Easy', time: '5 minutes',
    tools: [],
    steps: [
      'Go outside and find your dryer exhaust vent on an exterior wall.',
      'Run the dryer on a normal cycle and check if you can feel warm air blowing out.',
      'Inspect the vent flap — it should open when the dryer runs and close when it stops.',
      'Check for obstructions: bird nests, lint buildup, leaves, or a stuck flap.',
      'Clear any obstructions by hand or with a brush.',
      'Make sure the vent cap is not damaged or missing.',
    ],
    proTip: 'Bird nests in dryer vents are extremely common in spring. A clogged exterior vent is a fire hazard. Takes 2 minutes to check.',
    type: 'diy',
  },

  // ── DISHWASHER ────────────────────────────────────────────────────────────

  dishwasher_filter_cleaning: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Soft brush or old toothbrush', 'Dish soap', 'Warm water'],
    steps: [
      'Remove the bottom rack from the dishwasher.',
      'Locate the filter at the bottom of the tub — a cylindrical piece that twists out.',
      'Turn the cylindrical filter counterclockwise and lift it out.',
      'Rinse both pieces under warm running water.',
      'Use a soft brush with dish soap to gently scrub away grease and food particles.',
      'Rinse thoroughly until the water runs clear.',
      'Replace the flat filter first, then twist the cylindrical filter clockwise until it locks.',
    ],
    proTip: 'A clogged dishwasher filter is the #1 reason dishes come out cloudy or smelly. If yours has never been cleaned, don\'t be surprised by what you find.',
    type: 'diy',
  },

  dishwasher_spray_arm_cleaning: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Toothpick or thin wire', 'Warm water', 'White vinegar'],
    steps: [
      'Remove the bottom rack from the dishwasher.',
      'Locate the lower spray arm and unscrew or lift it off.',
      'Hold the spray arm under warm running water and look through each spray hole.',
      'Use a toothpick to poke through any clogged holes.',
      'Soak the arms in warm water with a splash of white vinegar for 10 minutes for heavy buildup.',
      'Rinse and reattach securely.',
      'Run an empty short cycle to confirm water sprays freely.',
    ],
    proTip: 'If the top rack items never come out clean, the upper spray arm is almost always the culprit.',
    type: 'diy',
  },

  dishwasher_descaling_treatment: {
    difficulty: 'Easy', time: '1 hour (machine runs itself)',
    tools: ['Dishwasher descaler or 2 cups white vinegar'],
    steps: [
      'Make sure the dishwasher is completely empty.',
      'If using a commercial descaler: place it in the bottom of the tub per package instructions.',
      'If using vinegar: place a dishwasher-safe bowl filled with 2 cups of white vinegar on the bottom rack.',
      'Run a hot water cycle with no detergent.',
      'When done, sprinkle 1 cup of baking soda across the bottom and run a short hot cycle.',
      'Wipe down the inside of the door and the door gasket.',
    ],
    proTip: 'Hard water leaves mineral deposits on the heating element. Regular descaling prevents white film on glasses and keeps your dishwasher efficient.',
    type: 'auto_order',
  },

  // ── OVEN ──────────────────────────────────────────────────────────────────

  oven_deep_clean: {
    difficulty: 'Moderate', time: '3–4 hours (with self-clean) or 1 hour (manual)',
    tools: ['Oven cleaner or baking soda + vinegar', 'Rubber gloves', 'Sponge', 'Scraper'],
    steps: [
      'Remove oven racks and soak them in hot soapy water.',
      'For self-cleaning ovens: remove racks first, close the door, and run the self-clean cycle. Ventilate the kitchen.',
      'For manual cleaning: make a paste of 1/2 cup baking soda and water. Spread over interior surfaces.',
      'Let the paste sit overnight or at least 2 hours.',
      'Spray white vinegar over the paste — it will foam. Wipe everything out with a damp sponge.',
      'Use a plastic scraper to loosen baked-on deposits.',
      'Scrub the soaked racks, rinse, dry, and replace.',
    ],
    proTip: 'For gas ovens, remove burner grates and caps and soak them while you clean the interior. A clean burner cap means better, more even flame.',
    type: 'diy',
  },

  oven_burner_inspection: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Toothpick or thin wire', 'Damp cloth'],
    steps: [
      'Make sure all burners are off and the stove is completely cool.',
      'Remove grates and burner caps. Lift out the burner heads.',
      'Inspect each burner port — they should all be clear.',
      'Use a toothpick to clear any clogged ports.',
      'Wipe burner heads with a damp cloth and dry thoroughly before replacing.',
      'Test each burner — flame should be blue and even. Yellow flame means a port is still clogged.',
    ],
    proTip: 'A yellow or orange gas flame means incomplete combustion and possible carbon monoxide production. Clean the ports — if the problem persists, call a gas technician.',
    type: 'diy',
  },

  // ── MICROWAVE ─────────────────────────────────────────────────────────────

  microwave_interior_clean: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Microwave-safe bowl', 'White vinegar or lemon', 'Water', 'Sponge or cloth'],
    steps: [
      'Fill a microwave-safe bowl with 1 cup water and 1/4 cup white vinegar or lemon juice.',
      'Microwave on high for 3–5 minutes until it steams up.',
      'Leave the door closed for 5 minutes to let steam loosen dried food.',
      'Carefully remove the bowl.',
      'Remove the turntable plate and wash it in the sink.',
      'Wipe down the interior walls, ceiling, floor, and door.',
      'Dry with a clean cloth and replace the turntable.',
    ],
    proTip: 'The steam method does all the work — no harsh chemicals needed. Never use abrasive scrubbers inside a microwave.',
    type: 'diy',
  },

  microwave_filter_replacement: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['Replacement filter (check your model number)'],
    steps: [
      'For the grease filter: locate the rectangular metal mesh filter on the bottom of the microwave.',
      'Push the tab or slide to release it and pull down.',
      'Wash the grease filter in hot soapy water or the dishwasher. Let it dry completely.',
      'For the charcoal filter (not washable): open the vent grille on top of the microwave.',
      'Slide out the old charcoal filter and slide in the new one.',
      'Snap the grille back in place.',
    ],
    proTip: 'If your kitchen smells like old food even when you\'re not cooking, a saturated charcoal filter is usually why. It must be replaced, not cleaned.',
    type: 'auto_order',
  },

  // ── GARBAGE DISPOSAL ──────────────────────────────────────────────────────

  garbage_disposal_cleaning_deodorizing: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Ice cubes', 'Rock salt', 'Lemon or lime', 'Dish soap'],
    steps: [
      'Run cold water and turn on the disposal.',
      'Drop in a handful of ice cubes followed by a handful of rock salt. Run for 30 seconds.',
      'Cut a lemon into wedges and drop them in one at a time while the disposal runs.',
      'Squirt a small amount of dish soap into the disposal with cold water running, run for 10 seconds.',
      'Turn off the disposal but keep the water running for another 15 seconds.',
      'Wipe down the rubber splash guard with a paper towel and dish soap.',
    ],
    proTip: 'Never pour grease down the disposal. Run cold water (not hot) when using the disposal — cold keeps fats solid so they grind up and flush away.',
    type: 'auto_order',
  },

  garbage_disposal_blade_inspection: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Flashlight', 'Disposal wrench or Allen wrench', 'Tongs'],
    warning: 'Never put your hand inside a garbage disposal — even when unplugged. Always use tongs or a long tool.',
    steps: [
      'Turn off the disposal switch and unplug the unit from under the sink.',
      'Shine a flashlight down into the disposal from above.',
      'Use tongs to check for any objects that may have fallen in. Remove anything you find.',
      'Look at the impeller blades for obvious damage or corrosion.',
      'If the disposal hums but doesn\'t spin, insert an Allen wrench into the hole at the bottom center and turn back and forth to free the jam.',
      'Press the red reset button on the bottom of the unit.',
      'Plug back in and test.',
    ],
    proTip: 'Most disposal "failures" are just jams — the reset button and an Allen wrench fix 90% of them in under 5 minutes.',
    type: 'diy',
  },

  // ── SMOKE DETECTOR ────────────────────────────────────────────────────────

  smoke_detector_battery_replacement: {
    difficulty: 'Easy', time: '5 minutes per detector',
    tools: ['Replacement 9V or AA batteries'],
    steps: [
      'Locate each smoke detector in your home.',
      'Twist the detector counterclockwise to remove it from the mounting bracket.',
      'Open the battery compartment on the back.',
      'Remove the old battery and insert the new one, matching the + and - terminals.',
      'Reattach the detector to the bracket.',
      'Press the test button for 3–5 seconds — you should hear a loud beep.',
    ],
    proTip: 'Change all batteries on the same day once a year — pick a memorable date. Having different batteries on different schedules means you\'ll always have one chirping at 3am.',
    type: 'auto_order',
  },

  'smoke_detector_test_&_inspection': {
    difficulty: 'Easy', time: '2 minutes per detector',
    tools: [],
    steps: [
      'Stand beneath each smoke detector.',
      'Press and hold the test button for 3–5 seconds.',
      'The alarm should sound loudly. If it chirps weakly, replace the battery.',
      'Check the manufacturing date on the back — smoke detectors should be replaced every 10 years.',
      'Check that each detector is firmly attached and the cover is clean and free of dust.',
    ],
    proTip: 'Test your detectors monthly — it takes 30 seconds and can save your life. Test on the first of every month.',
    type: 'diy',
  },

  smoke_detector_full_replacement: {
    difficulty: 'Easy', time: '15 minutes per detector',
    tools: ['New smoke detector', 'Screwdriver', 'Drill (possibly)'],
    steps: [
      'Purchase a replacement detector. Combination smoke/CO detectors are recommended.',
      'Turn off power at the breaker if your detector is hardwired.',
      'Remove the old detector from its mounting bracket.',
      'If hardwired: disconnect the wire harness connector.',
      'Attach the new mounting bracket using existing holes if they align.',
      'If hardwired: connect the new wire harness and tuck wires into the bracket.',
      'Attach the new detector and restore power. Test it.',
    ],
    proTip: 'Smoke detectors over 10 years old are significantly less sensitive. Check the date on yours today — this is a life-safety item.',
    type: 'auto_order',
  },

  // ── GARAGE DOOR ───────────────────────────────────────────────────────────

  garage_door_lubricate_moving_parts: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Garage door lubricant spray (white lithium grease or silicone — NOT WD-40)'],
    steps: [
      'Open the garage door manually or with the opener.',
      'Apply lubricant to the hinges where the door panels fold.',
      'Lubricate the rollers — spray inside the roller where it meets the stem.',
      'Spray the torsion spring(s) above the door — a light coat along the full length.',
      'Apply a thin line of lubricant along the inside of the curved track sections only.',
      'Wipe away any excess with a cloth.',
      'Operate the door 3–4 times to distribute the lubricant.',
    ],
    proTip: 'WD-40 is NOT a lubricant — it will dry out your garage door components over time. Use white lithium grease or a dedicated garage door lubricant.',
    type: 'auto_order',
  },

  garage_door_safety_sensor_test: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['A cardboard box or rolled up towel'],
    steps: [
      'Close the garage door.',
      'Place a cardboard box flat on the ground in the center of the door path.',
      'Press the button to close the door — it should hit the object and immediately reverse.',
      'With the door open, wave your hand through the beam between the two sensors near the floor.',
      'The door should stop or reverse if it was closing.',
      'Clean the sensor lenses with a soft cloth and check that both sensors point directly at each other.',
    ],
    proTip: 'Safety sensors prevent the door from closing on a child or pet. If yours fail this test, do not use the door until they\'re fixed.',
    type: 'diy',
  },

  garage_door_full_service_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Torsion springs are under extreme tension and can cause serious injury if mishandled — this should always be done by a professional.',
    steps: [
      'Schedule a garage door service company for an annual tune-up.',
      'Note any issues beforehand: unusual noises, slow operation, gaps when closed.',
      'Ask the technician to inspect: torsion springs, cables, pulleys, track alignment, roller condition, and opener settings.',
      'Ask them to lubricate all moving parts as part of the service.',
      'If springs are original and the door is over 7 years old, ask about remaining spring life.',
    ],
    proTip: 'A spring replacement costs $150–$250. A service visit that catches a worn spring before it breaks is $75–$100. The math is easy.',
    type: 'contractor',
  },

  // ── OTHER ─────────────────────────────────────────────────────────────────

  other_general_inspection: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Flashlight', 'Notepad'],
    steps: [
      'Visually inspect the appliance for any signs of damage, rust, or unusual wear.',
      'Check all connections — power cords, hoses, or gas lines — for cracks or loose fittings.',
      'Listen for any unusual sounds during operation.',
      'Check for any error codes or indicator lights on the control panel.',
      'Clean the exterior and any accessible interior surfaces.',
      'Note anything unusual and consult your owner\'s manual for guidance.',
    ],
    proTip: 'Keep a simple log of when you\'ve serviced each appliance and what you found. This history is invaluable when troubleshooting and increases your home\'s value when you sell.',
    type: 'diy',
  },

  // ── SEPTIC SYSTEM ─────────────────────────────────────────────────────────

  septic_tank_pumping: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Tank pumping must always be done by a licensed septic pumping company. Do not attempt to open or service your septic tank yourself — toxic gases can accumulate inside.',
    steps: [
      'Schedule a licensed septic pumping company every 3–5 years, or more frequently if you have a large household.',
      'Locate your septic tank before they arrive — it\'s typically 10–25 feet from the house, buried 4–8 inches underground. Check your property records or look for a clean-out cap.',
      'Ensure the pumping truck has clear access to the tank location.',
      'Ask the technician to inspect the baffles, inlet and outlet pipes, and the condition of the tank walls while pumping.',
      'Ask for a written report and note the date and how full the tank was when pumped.',
      'Keep a record of all service visits — this is required documentation if you ever sell your home.',
    ],
    proTip: 'The average 3-bedroom home with a 1,000-gallon tank needs pumping every 3–5 years. Waiting too long causes solids to flow into the drain field, which can cost $5,000–$20,000 to replace.',
    type: 'contractor',
  },

  'septic_inspection_&_baffle_check': {
    difficulty: 'Advanced', time: '1 hour (professional)',
    tools: [],
    contractorNote: 'A septic inspection should be performed by a licensed inspector or septic technician. They will check the baffles, tank condition, and drain field function.',
    steps: [
      'Schedule a licensed septic inspector every 3 years, or whenever you notice slow drains, odors, or wet spots in the yard.',
      'The inspector will locate and uncover the tank access lids.',
      'They will check the inlet and outlet baffles — these prevent scum and solids from flowing into the drain field.',
      'They will probe the drain field to check for saturation or failure.',
      'Ask for a written inspection report with photos.',
      'Address any recommended repairs promptly — small issues become expensive ones quickly.',
    ],
    proTip: 'Many states require a passing septic inspection before a home can be sold. Keep all inspection records in your Casavio profile — they add tangible value at resale.',
    type: 'contractor',
  },

  septic_drain_field_inspection: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Your eyes and nose'],
    steps: [
      'Walk the area above your drain field — this is typically a flat, open area in your yard away from trees.',
      'Look for any unusually green or lush grass compared to surrounding areas — this can indicate a saturated or failing field.',
      'Look for wet or soggy ground, standing water, or visible sewage odors near the field.',
      'Check that no vehicles, heavy equipment, or structures have been placed over the drain field — this compacts the soil.',
      'Make sure tree roots aren\'t encroaching — roots can destroy drain field pipes.',
      'If you notice any of the warning signs above, call a septic professional promptly.',
    ],
    proTip: 'Never plant trees or shrubs near your drain field — roots are the #1 cause of drain field destruction. Keep the area in grass only.',
    type: 'diy',
  },

  septic_bacteria_treatment: {
    difficulty: 'Easy', time: '2 minutes',
    tools: ['Septic treatment tablets or packets (e.g. Rid-X or equivalent)'],
    steps: [
      'Purchase a monthly septic bacteria treatment — available at hardware stores.',
      'Drop the recommended amount directly into the toilet.',
      'Flush once.',
      'That\'s it — the bacteria culture flows into the tank and helps break down solids.',
    ],
    proTip: 'Avoid antibacterial soaps, bleach, and harsh drain cleaners — they kill the beneficial bacteria in your septic tank that do the work of breaking down waste. Enzyme treatments help replenish them.',
    type: 'auto_order',
  },

  // ── SOLAR PANELS ──────────────────────────────────────────────────────────

  solar_panel_cleaning: {
    difficulty: 'Moderate', time: '30–60 minutes',
    tools: ['Soft brush or squeegee with extension pole', 'Garden hose', 'Mild dish soap (optional)', 'Ladder (if needed)'],
    warning: 'Never use a pressure washer on solar panels — it can damage the surface and void your warranty. Work in the early morning or evening when panels are cool.',
    steps: [
      'Check your system\'s production monitoring app first — a significant drop in output often means dirty panels.',
      'Work in the early morning or evening when panels are cool. Hot panels can crack when hit with cold water.',
      'Use a garden hose to rinse the panels from above, letting water flow downward.',
      'For stuck-on dirt or bird droppings, use a soft brush or squeegee with an extension pole and mild soapy water.',
      'Rinse thoroughly — soap residue can attract dirt.',
      'Check your production output over the next few days to confirm improvement.',
    ],
    proTip: 'In most areas, rain cleans panels adequately. Manual cleaning is most valuable in dry climates or dusty areas. Studies show dirty panels can lose 15–25% of output.',
    type: 'diy',
  },

  solar_inverter_check: {
    difficulty: 'Easy', time: '10 minutes',
    tools: [],
    steps: [
      'Locate your inverter — it\'s typically a box mounted on an interior or exterior wall near your electrical panel.',
      'Check the status light or display. A green light or "Normal" reading means it\'s operating correctly.',
      'A red or orange light, error code, or blank display indicates a problem.',
      'Check your monitoring app (SolarEdge, Enphase, etc.) to confirm current and historical production.',
      'Compare today\'s production to the same date last year — a 10%+ drop warrants a service call.',
      'Make sure the inverter has adequate ventilation and isn\'t covered or obstructed.',
    ],
    proTip: 'Your inverter has a typical lifespan of 10–15 years — shorter than your panels. Monitoring it annually helps you plan for replacement before it fails unexpectedly.',
    type: 'diy',
  },

  solar_full_system_inspection: {
    difficulty: 'Advanced', time: '2–3 hours (professional)',
    tools: [],
    contractorNote: 'A professional solar inspection involves checking wiring connections, mounting hardware, panel condition, inverter performance, and roof penetration seals. Always use a licensed solar contractor.',
    steps: [
      'Schedule with your original installer or a licensed solar contractor every 2 years.',
      'Ask them to inspect: panel surfaces for cracks or delamination, all wiring and connections for corrosion, mounting hardware for tightness and rust, roof penetrations for seal integrity, and inverter performance data.',
      'Request a thermal imaging scan if available — hot spots indicate failing cells.',
      'Ask for a written report with production comparison data.',
    ],
    proTip: 'Solar systems generally require very little maintenance but a bi-annual inspection protects your investment and your roof. Most issues caught early are inexpensive to fix.',
    type: 'contractor',
  },

  solar_monitor_production_output: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['Your monitoring app (SolarEdge, Enphase, SunPower, etc.)'],
    steps: [
      'Open your solar monitoring app on your phone or computer.',
      'Check today\'s production in kWh.',
      'Compare to your system\'s expected output for this time of year.',
      'Look at the monthly trend — production should be highest in summer and lowest in winter.',
      'If production has dropped more than 10–15% compared to the same period last year, check for dirty panels, shading from tree growth, or inverter issues.',
      'Note any error alerts or offline panels in the app.',
    ],
    proTip: 'Most solar systems have free monitoring apps that show real-time and historical data. Set a monthly calendar reminder to check — catching a failed panel early prevents months of lost production.',
    type: 'diy',
  },

  // ── POOL / SPA ────────────────────────────────────────────────────────────

  pool_chemical_balance_check: {
    difficulty: 'Moderate', time: '15 minutes',
    tools: ['Pool test kit or test strips', 'pH increaser/decreaser', 'Chlorine tablets or liquid chlorine', 'Alkalinity increaser'],
    steps: [
      'Collect a water sample from elbow-depth in the pool, away from return jets.',
      'Test pH — ideal range is 7.2–7.6. Below 7.2 add pH increaser; above 7.6 add pH decreaser.',
      'Test free chlorine — ideal range is 1–3 ppm. Add chlorine if below 1 ppm.',
      'Test total alkalinity — ideal range is 80–120 ppm. Add alkalinity increaser if below 80 ppm.',
      'Test cyanuric acid (stabilizer) — ideal range is 30–50 ppm for outdoor pools.',
      'Add any needed chemicals one at a time, waiting 15–30 minutes between additions.',
      'Run the pump for at least 1 hour after adding chemicals.',
    ],
    proTip: 'Test your water at the same time each week — early morning before the sun affects chlorine levels. Proper chemical balance prevents algae, protects equipment, and keeps swimmers safe.',
    type: 'diy',
  },

  pool_filter_cleaning: {
    difficulty: 'Moderate', time: '30–60 minutes',
    tools: ['Garden hose with nozzle', 'Filter cleaner solution (for cartridge filters)'],
    steps: [
      'Turn off the pool pump.',
      'For cartridge filters: open the filter housing, remove the cartridge, and rinse thoroughly with a garden hose from top to bottom. Soak in filter cleaner solution overnight for deep cleaning.',
      'For sand filters: run a backwash cycle by setting the valve to "Backwash" and running the pump until the sight glass runs clear. Then set to "Rinse" for 30 seconds.',
      'For DE filters: backwash until clear, then add fresh diatomaceous earth through the skimmer.',
      'Reassemble the filter and restore power to the pump.',
      'Check the pressure gauge — normal operating pressure after cleaning should be 8–15 psi lower than before.',
    ],
    proTip: 'A dirty filter reduces water circulation, which leads to algae growth. If your pump pressure is 8–10 psi above normal, it\'s past time for a cleaning.',
    type: 'diy',
  },

  pool_shock_treatment: {
    difficulty: 'Moderate', time: '15 minutes',
    tools: ['Pool shock (calcium hypochlorite or non-chlorine shock)', 'Rubber gloves', 'Safety glasses', 'Bucket'],
    warning: 'Always add shock to water — never add water to shock. Wear gloves and eye protection. Add shock in the evening to prevent UV degradation.',
    steps: [
      'Test your water chemistry before shocking — pH should be between 7.2–7.4 for best results.',
      'Fill a bucket with pool water.',
      'Slowly add the shock to the bucket of water while stirring.',
      'Walk around the pool and pour the mixture evenly along the edges.',
      'Run the pump for at least 8 hours — ideally overnight.',
      'Do not swim until chlorine levels drop below 3 ppm.',
      'Test the water the next morning before use.',
    ],
    proTip: 'Shock your pool after heavy rain, after a pool party, when the water looks cloudy, or when you smell strong chlorine (which actually means there\'s not enough free chlorine).',
    type: 'auto_order',
  },

  pool_pump_motor_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'While you can inspect the pump visually yourself, any internal repairs should be handled by a licensed pool technician.',
    steps: [
      'Visually inspect the pump for leaks around the lid, seal plate, or plumbing fittings.',
      'Listen for unusual noises — grinding, squealing, or loud humming can indicate bearing failure.',
      'Check the pump basket and clear any debris.',
      'Verify the pump is priming properly — if it runs dry or loses prime, check for air leaks.',
      'For annual professional service: schedule with a pool company to inspect the motor windings, impeller, seal, and all plumbing connections.',
    ],
    proTip: 'A variable-speed pump running at lower speeds can cut pool energy costs by 50–80% compared to single-speed pumps. If your pump is over 10 years old, the upgrade pays for itself quickly.',
    type: 'contractor',
  },

  pool_winter_winterization: {
    difficulty: 'Advanced', time: '2–4 hours',
    tools: ['Air compressor or shop vac', 'Winter pool cover', 'Pool antifreeze', 'Rubber plugs', 'Pool closing chemical kit'],
    contractorNote: 'In mild climates like Northern California, full winterization may not be required — consult a local pool professional about the right approach for your area.',
    steps: [
      'Balance the water chemistry 1 week before closing: pH 7.2–7.6, alkalinity 80–120 ppm, calcium hardness 175–225 ppm.',
      'Shock the pool and add algaecide.',
      'Clean the pool thoroughly — brush walls, vacuum the floor, empty skimmer baskets.',
      'Lower the water level below the skimmer.',
      'Drain all equipment — pump, filter, heater, and chlorinator.',
      'Blow out the plumbing lines with an air compressor and plug the returns.',
      'Add pool antifreeze to the lines if freezing temps are possible.',
      'Install the winter cover securely.',
    ],
    proTip: 'In Northern California, many pool owners keep their pools running year-round with reduced maintenance. Talk to a local pool company about whether full winterization or just a reduced-maintenance schedule makes more sense for your climate.',
    type: 'contractor',
  },

  // ── WELL WATER ────────────────────────────────────────────────────────────

  well_water_quality_test: {
    difficulty: 'Moderate', time: '30 minutes (plus lab time)',
    tools: ['Certified water testing kit or mail-in lab kit'],
    steps: [
      'Purchase a certified water testing kit from a hardware store or order a mail-in kit from a state-certified lab.',
      'Run cold water from your tap for 2–3 minutes before collecting the sample.',
      'Collect the sample in the sterile bottle provided, following the instructions carefully to avoid contamination.',
      'Test for at minimum: coliform bacteria, nitrates, pH, hardness, and any contaminants common to your area.',
      'If sending to a lab, ship promptly and refrigerate if storing overnight.',
      'Review the results against EPA safe drinking water standards.',
      'Address any failed parameters with a licensed water treatment specialist.',
    ],
    proTip: 'Well water quality can change seasonally — especially after heavy rains which can introduce surface contaminants. Test annually and immediately after any flooding near your well.',
    type: 'contractor',
  },

  well_pressure_tank_inspection: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Tire pressure gauge'],
    steps: [
      'Turn off the power to the well pump at the breaker.',
      'Drain the pressure tank by opening a faucet until water stops flowing.',
      'Locate the air valve on top of the pressure tank — it looks like a tire valve.',
      'Use a tire pressure gauge to check the air pressure.',
      'Pressure should be 2 psi below the pump cut-in pressure (typically 28 psi for a 30/50 switch).',
      'If pressure is too low, add air with a bike pump or compressor.',
      'If you get water from the air valve instead of air, the bladder is ruptured — the tank needs replacement.',
      'Restore power to the pump.',
    ],
    proTip: 'A waterlogged pressure tank causes the pump to cycle on and off rapidly, wearing it out prematurely. This simple annual check can double your pump\'s lifespan.',
    type: 'diy',
  },

  well_well_cap_inspection: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Flashlight'],
    steps: [
      'Locate your well casing — it\'s usually a 4–6 inch diameter pipe sticking 8–12 inches above ground.',
      'Inspect the well cap on top — it should be tightly secured with no cracks or gaps.',
      'Check for insects, rodents, or debris that may have gotten under a loose cap.',
      'Look for any surface water drainage running toward the well — this is a contamination risk.',
      'Check for corrosion or damage on the casing.',
      'If the cap is cracked, missing, or unsealed, replace it immediately — an open well is a contamination and safety hazard.',
    ],
    proTip: 'The well cap is your primary barrier against surface contaminants entering your drinking water. A new well cap costs $15–$30 and is one of the easiest and most important maintenance items.',
    type: 'diy',
  },

  well_pump_system_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Well pump inspection involves electrical testing, pressure measurements, and often pulling the pump from the well — always done by a licensed well contractor.',
    steps: [
      'Schedule with a licensed well contractor every 2 years or if you notice: low water pressure, pump running constantly, air sputtering from faucets, or discolored water.',
      'The contractor will check pump output, pressure switch settings, wiring connections, and pump amperage draw.',
      'Ask them to inspect the pitless adapter — the underground connection between the well casing and your home plumbing.',
      'Ask about the age of your pump — most submersible pumps last 10–15 years.',
      'Request a written report with all measurements.',
    ],
    proTip: 'Submersible well pumps typically give warning signs before failing completely — reduced flow, pressure fluctuations, or unusual cycling. Catching these early prevents a no-water emergency.',
    type: 'contractor',
  },

  // ── PROPANE ───────────────────────────────────────────────────────────────

  propane_tank_level_check: {
    difficulty: 'Easy', time: '2 minutes',
    tools: ['Warm water and your hand (the pour method)'],
    steps: [
      'For tanks with a gauge: simply read the percentage shown on the dial on top of the tank.',
      'For tanks without a gauge — the warm water method: pour warm (not hot) water down the side of the tank.',
      'Run your hand along the tank from top to bottom.',
      'The tank will feel cool where there is propane and warm where it is empty.',
      'The line between warm and cool is approximately your propane level.',
      'Order a delivery when you reach 20–25% to avoid running out.',
    ],
    proTip: 'Never let your propane tank run completely empty — air and moisture can enter the tank, and your gas company will need to perform a pressure test before refilling, which costs extra and takes time.',
    type: 'diy',
  },

  'propane_regulator_&_line_inspection': {
    difficulty: 'Advanced', time: '1 hour (professional)',
    tools: [],
    contractorNote: 'Gas line and regulator inspections should be performed by a licensed propane technician. Do not attempt to inspect or repair gas connections yourself.',
    steps: [
      'Schedule an annual inspection with your propane supplier — most offer this as part of a service plan.',
      'The technician will inspect the regulator for wear, corrosion, and proper pressure output.',
      'They will check all above-ground gas lines for damage, corrosion, and proper support.',
      'They will check all connections at appliances for leaks using a gas leak detector or soapy water solution.',
      'Ask them to verify that the shut-off valves at each appliance operate correctly.',
    ],
    proTip: 'Propane regulators typically last 10–15 years. If yours is original equipment on an older home, ask your technician about replacement during the inspection.',
    type: 'contractor',
  },

  propane_leak_test: {
    difficulty: 'Advanced', time: '30 minutes (professional)',
    tools: [],
    contractorNote: 'Formal leak testing requires specialized equipment and should be performed by a licensed technician. However, you should know how to do a basic check yourself.',
    steps: [
      'For a basic visual/smell check: walk around your tank and all exposed gas lines.',
      'Propane has a distinct rotten egg or skunk smell added as a safety odorant. If you smell it strongly, leave immediately and call your propane supplier.',
      'For a simple DIY check on connections: mix dish soap and water into a solution. Apply to connections and fittings with a brush.',
      'If bubbles form, there is a gas leak at that fitting. Do not use any appliances — call your propane supplier.',
      'For a formal pressure test: contact your propane supplier for a licensed technician.',
    ],
    proTip: 'Install a propane/CO detector inside your home near the floor — propane is heavier than air and settles low. These detectors cost $30–$50 and provide 24/7 monitoring.',
    type: 'contractor',
  },

  propane_tank_recertification: {
    difficulty: 'Advanced', time: 'Professional service',
    tools: [],
    contractorNote: 'Tank recertification is required by law every 10–12 years and must be performed by a licensed propane company.',
    steps: [
      'Contact your propane supplier when your tank approaches its certification expiration date (check the collar of the tank for the manufacture date).',
      'The technician will inspect the tank for corrosion, dents, and valve condition.',
      'They will perform a pressure test to verify tank integrity.',
      'If the tank passes, they will stamp a new certification date on the collar.',
      'If the tank fails, it must be replaced — your supplier can coordinate this.',
    ],
    proTip: 'Keep records of your tank\'s certification date in your Casavio profile. An expired tank cannot legally be refilled, which can leave you without heat or cooking fuel at the worst time.',
    type: 'contractor',
  },

  // ── GENERATOR ─────────────────────────────────────────────────────────────

  generator_oil_filter_change: {
    difficulty: 'Moderate', time: '30–45 minutes',
    tools: ['Fresh engine oil (check your manual for type and quantity)', 'New oil filter', 'Oil drain pan', 'Wrench', 'Funnel'],
    steps: [
      'Run the generator for a few minutes to warm the oil — warm oil drains more completely.',
      'Turn the generator off and let it cool for 5–10 minutes.',
      'Place an oil drain pan under the drain plug on the bottom of the engine.',
      'Remove the drain plug with a wrench and let the oil drain completely.',
      'Remove the old oil filter and replace it with the new one — hand-tighten only.',
      'Replace the drain plug and tighten securely.',
      'Add fresh oil through the fill cap using a funnel — check your manual for the correct amount.',
      'Start the generator briefly, then check for leaks around the drain plug and filter.',
      'Dispose of old oil properly at a recycling center or auto parts store.',
    ],
    proTip: 'Fresh oil is the single most important thing you can do to extend engine life. A generator that sits unused also needs an oil change annually — oil degrades over time even without use.',
    type: 'diy',
  },

  generator_spark_plug_replacement: {
    difficulty: 'Easy', time: '20 minutes',
    tools: ['Spark plug socket (typically 5/8")', 'Replacement spark plug (check your manual)', 'Spark plug gap tool'],
    steps: [
      'Turn the generator off and disconnect the spark plug wire.',
      'Remove the spark plug using a spark plug socket.',
      'Inspect the old plug — black sooty deposits indicate rich fuel mixture; white deposits indicate lean mixture or overheating.',
      'Check the gap on the new plug using a gap tool — adjust to the specification in your manual (typically 0.028–0.031").',
      'Thread the new plug in by hand to avoid cross-threading, then tighten with the socket wrench (finger tight plus 1/2 turn).',
      'Reconnect the spark plug wire.',
      'Start the generator to confirm it runs smoothly.',
    ],
    proTip: 'A worn spark plug causes hard starting, rough running, and increased fuel consumption. A new plug costs $3–$8 and takes 20 minutes. It\'s one of the best values in small engine maintenance.',
    type: 'diy',
  },

  generator_battery_check: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Multimeter or battery tester'],
    steps: [
      'Locate the battery on your generator — typically a small 12V battery similar to a motorcycle battery.',
      'Check the battery terminals for corrosion — a white or greenish buildup. Clean with a wire brush if needed.',
      'Use a multimeter set to DC voltage to check the battery — it should read 12.4–12.7V at rest.',
      'Below 12.0V indicates a discharged or failing battery.',
      'If your generator has an electric start and won\'t crank, the battery is the first thing to check.',
      'Trickle chargers or battery maintainers are recommended if the generator sits unused for long periods.',
    ],
    proTip: 'A dead battery is the #1 reason generators fail to start in an emergency. Keep a trickle charger connected during storage — they cost $20 and eliminate this problem entirely.',
    type: 'diy',
  },

  generator_load_test_run: {
    difficulty: 'Easy', time: '30 minutes',
    tools: ['Extension cord', 'A load to run (space heater, lights, etc.)'],
    steps: [
      'Move the generator outside — never run a generator indoors or in a garage due to carbon monoxide.',
      'Position it at least 20 feet from windows, doors, and vents.',
      'Check the oil level before starting.',
      'Start the generator and let it warm up for 2–3 minutes at no load.',
      'Connect a load — a space heater or several lights — and run for at least 20 minutes.',
      'Check for unusual noises, vibrations, or exhaust smoke during the run.',
      'Let it run unloaded for 2 minutes before shutting down.',
      'Note the date of the test run in your Casavio notes.',
    ],
    proTip: 'Generators need to be run under load periodically to keep the engine lubricated, burn off moisture, and keep the fuel system from gumming up. Monthly is ideal; quarterly at minimum.',
    type: 'diy',
  },

  generator_full_service_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Annual professional service for generators includes carburetor cleaning, fuel system inspection, load testing, and for standby generators, transfer switch testing.',
    steps: [
      'Schedule annual service with a generator technician or your generator\'s dealer.',
      'Ask them to service: carburetor, air filter, fuel filter, spark plug, oil and filter, battery, and all electrical connections.',
      'For standby generators: ask them to test the automatic transfer switch and confirm it operates correctly.',
      'Ask for a written service record.',
      'Address any recommended repairs before the next storm season.',
    ],
    proTip: 'A generator that isn\'t serviced regularly will almost certainly fail the first time you actually need it. Annual professional service is inexpensive insurance.',
    type: 'contractor',
  },

  // ── WHOLE-HOME BATTERY ────────────────────────────────────────────────────

  battery_monitor_charge_cycles: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['Your battery monitoring app (Tesla, Enphase, etc.)'],
    steps: [
      'Open your battery management app.',
      'Check the current state of charge — most systems target 20–80% for daily use to preserve battery health.',
      'Review the charge and discharge history for the past month.',
      'Confirm the battery charged from solar or grid during off-peak hours as configured.',
      'Check for any alerts or warnings in the app.',
      'Note the total energy stored and discharged — this helps track long-term capacity degradation.',
    ],
    proTip: 'Lithium battery systems like the Powerwall are designed to last 10+ years with proper charge management. Most systems handle this automatically — your job is to monitor for alerts and review performance monthly.',
    type: 'diy',
  },

  battery_software_firmware_update: {
    difficulty: 'Easy', time: '5 minutes (system updates automatically)',
    tools: ['Your battery monitoring app'],
    steps: [
      'Open your battery management app and check for any available firmware updates.',
      'For Tesla Powerwall: updates are typically pushed automatically when connected to WiFi. Confirm your gateway is connected.',
      'For Enphase: check the Enlighten app for any system alerts about firmware.',
      'Do not interrupt power to the system during an update.',
      'After an update, confirm the system resumes normal operation.',
      'Contact your installer if an update fails or the system behaves abnormally after an update.',
    ],
    proTip: 'Firmware updates often improve battery management algorithms, which can extend battery life and improve performance. Keep your system connected to WiFi to receive them automatically.',
    type: 'diy',
  },

  battery_connection_wiring_inspection: {
    difficulty: 'Advanced', time: '1 hour (professional)',
    tools: [],
    contractorNote: 'Never open or service the internal components of a home battery system yourself — they contain high-voltage DC electricity that can be lethal. All internal inspections must be done by a licensed installer.',
    steps: [
      'Schedule with your original installer or a licensed battery system technician annually.',
      'Ask them to inspect all external wiring connections for corrosion or looseness.',
      'Ask them to verify the gateway/inverter is communicating properly with the battery.',
      'Ask them to check the disconnect switch operation.',
      'Ask them to verify grounding connections are intact.',
      'Review any alerts from the past year with the technician.',
    ],
    proTip: 'Home battery systems have very few moving parts and typically require minimal maintenance. The key is catching any wiring or connection issues early before they cause system downtime.',
    type: 'contractor',
  },

  battery_full_system_performance_test: {
    difficulty: 'Advanced', time: '2–3 hours (professional)',
    tools: [],
    contractorNote: 'A full performance test involves measuring actual capacity against rated capacity and is performed by your installer with specialized equipment.',
    steps: [
      'Schedule a performance test with your installer every 1–2 years.',
      'The technician will fully charge then fully discharge the battery while measuring actual energy output.',
      'They will compare measured capacity to the original rated capacity.',
      'A healthy lithium battery should retain at least 80% of original capacity after 10 years.',
      'They will check the battery management system for any degraded cells.',
      'Review the results and ask about warranty coverage if capacity has dropped significantly.',
    ],
    proTip: 'Tesla Powerwall comes with a 10-year warranty guaranteeing 70% capacity retention. Keep all service records in Casavio — they\'re needed to make warranty claims.',
    type: 'contractor',
  },

  // ── SUMP PUMP ─────────────────────────────────────────────────────────────

  sump_pump_test_pump_operation: {
    difficulty: 'Easy', time: '10 minutes',
    tools: ['Bucket of water'],
    steps: [
      'Locate your sump pump in the sump pit in your basement or crawl space.',
      'Pour a bucket of water slowly into the sump pit.',
      'The pump should activate automatically when the water reaches the float trigger level.',
      'Confirm water is being pumped out through the discharge line.',
      'The pump should shut off automatically when the water level drops.',
      'If the pump doesn\'t activate, check that it\'s plugged in and the float isn\'t stuck.',
      'If it activates but water doesn\'t pump out, the impeller may be clogged or the discharge line blocked.',
    ],
    proTip: 'Test your sump pump before rainy season, not during. A pump that fails during a heavy storm can mean thousands in water damage. This test takes 5 minutes and could save your basement.',
    type: 'diy',
  },

  sump_pump_clean_pump_pit: {
    difficulty: 'Easy', time: '30 minutes',
    tools: ['Wet/dry vacuum', 'Garden hose', 'Scrub brush', 'Work gloves'],
    steps: [
      'Unplug the sump pump.',
      'Remove the pump from the pit — most just lift out.',
      'Use a wet/dry vacuum to remove any standing water and debris from the pit.',
      'Rinse the pit walls with a garden hose and scrub off any sediment or buildup.',
      'Vacuum out the rinse water.',
      'Clean the pump itself — rinse the impeller housing and remove any debris.',
      'Check the intake screen on the bottom of the pump for clogs.',
      'Lower the pump back into the pit and plug it back in.',
      'Test with a bucket of water to confirm it still operates correctly.',
    ],
    proTip: 'Sediment and gravel accumulation in the pit can clog the pump impeller, causing it to burn out. Annual cleaning prevents the most common cause of sump pump failure.',
    type: 'diy',
  },

  sump_pump_check_backup_battery: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Multimeter'],
    steps: [
      'Locate the backup battery system — typically connected to the sump pump or a separate backup pump.',
      'Check the battery charge indicator if your system has one.',
      'Use a multimeter to check the battery voltage — it should read 12.4–12.7V for a healthy 12V battery.',
      'Check the battery terminals for corrosion and clean if needed.',
      'Test the backup system by unplugging the primary pump and pouring water into the pit — the backup should activate.',
      'Restore the primary pump after testing.',
      'Most backup batteries last 3–5 years and should be replaced proactively.',
    ],
    proTip: 'Your sump pump backup battery exists for one reason: when the power goes out during a storm — the exact moment you\'re most likely to get water. Make sure it\'s ready.',
    type: 'diy',
  },

  sump_pump_inspect_discharge_line: {
    difficulty: 'Easy', time: '10 minutes',
    tools: [],
    steps: [
      'Follow the discharge pipe from the sump pump to where it exits your home.',
      'Check for any kinks, cracks, or disconnected sections inside.',
      'Go outside and locate where the discharge pipe exits — usually on the foundation wall or through a buried line.',
      'Confirm the discharge point is at least 10 feet from the foundation — water discharging close to the house can flow back into the basement.',
      'Check that the discharge outlet is not blocked by dirt, ice, or vegetation.',
      'In winter, check that the outlet hasn\'t frozen shut — a frozen discharge line will cause the pump to run continuously and burn out.',
    ],
    proTip: 'Discharge line issues are responsible for many sump pump failures. A pump that runs continuously because the water can\'t go anywhere will burn out within hours.',
    type: 'diy',
  },

  // ── WHOLE-HOUSE FAN ───────────────────────────────────────────────────────

  whole_house_fan_belt_pulley_inspection: {
    difficulty: 'Moderate', time: '30 minutes',
    tools: ['Flashlight', 'Screwdriver'],
    steps: [
      'Turn off the fan and disconnect power at the breaker.',
      'Access the fan — usually in the attic via an attic hatch.',
      'Locate the belt connecting the motor to the fan pulley.',
      'Inspect the belt for: cracks, fraying, glazing (shiny surface), or excessive slack.',
      'Check belt tension — you should be able to deflect it about 3/4 inch with moderate finger pressure.',
      'Inspect both pulleys for wobble, cracks, or misalignment.',
      'If the belt is worn or too loose, it needs replacement — note the size printed on the old belt.',
      'Check all mounting bolts for tightness.',
    ],
    proTip: 'A slipping or worn belt causes the fan to run slower and louder. Belt replacements cost $10–$20 and take 15 minutes — far cheaper than a motor replacement from a seized fan.',
    type: 'diy',
  },

  whole_house_fan_lubricate_bearings: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['SAE 20 non-detergent motor oil or a few drops of 3-in-1 oil'],
    steps: [
      'Turn off the fan and disconnect power at the breaker.',
      'Access the fan in the attic.',
      'Locate the oil ports on the motor — small rubber plugs or metal caps on the sides of the motor housing.',
      'Remove the oil port cap.',
      'Add 2–3 drops of SAE 20 non-detergent motor oil. Do not over-oil — excess oil attracts dust.',
      'Replace the oil port cap.',
      'Some newer motors are sealed and do not require oiling — check your manual.',
    ],
    proTip: 'Under-lubricated motor bearings are the most common cause of whole-house fan failure and the squealing noise that develops over time. Annual oiling costs pennies and extends motor life by years.',
    type: 'diy',
  },

  'whole_house_fan_clean_louvers_&_shutters': {
    difficulty: 'Easy', time: '20 minutes',
    tools: ['Vacuum with brush attachment', 'Damp cloth'],
    steps: [
      'The louvers are the ceiling grille visible from inside your home that open when the fan runs.',
      'Turn off the fan.',
      'From inside the room, vacuum the louver slats using the brush attachment.',
      'Wipe each slat with a damp cloth to remove dust buildup.',
      'Open the louvers manually if possible and check that they move freely.',
      'In the attic, check the shutter at the fan housing — it should open fully when the fan is on and close completely when off.',
      'Clean the attic shutter with a damp cloth and confirm it moves freely on its hinges.',
    ],
    proTip: 'Stuck or dirty louvers restrict airflow significantly, making your fan work harder and cool less effectively. A clean fan can cool a home by 5–10°F in minutes on a cool evening.',
    type: 'diy',
  },

  whole_house_fan_full_service: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Professional service includes motor testing, wiring inspection, belt adjustment or replacement, and damper sealing check.',
    steps: [
      'Schedule with an HVAC or fan technician every 3 years.',
      'Ask them to: test motor amperage draw, inspect wiring connections, adjust or replace the belt, check and lubricate all moving parts, and verify the damper seals completely in winter.',
      'Ask about insulating covers for the winter — an uninsulated whole-house fan opening is a major source of heat loss.',
    ],
    proTip: 'In winter, the whole-house fan opening in your ceiling is like a giant hole in your insulation. A proper insulating cover can save hundreds per year in heating costs.',
    type: 'contractor',
  },

  // ── WATER SOFTENER ────────────────────────────────────────────────────────

  water_softener_salt_level_check_refill: {
    difficulty: 'Easy', time: '5 minutes',
    tools: ['Water softener salt (pellets or crystals)'],
    steps: [
      'Open the brine tank lid — the larger of the two tanks in your softener system.',
      'Look inside and check the salt level. It should be at least half full and never below the water line.',
      'If salt is low, add bags of softener salt. Use the type recommended by your manufacturer — usually pellets.',
      'Break up any salt bridges — a hard crust that can form across the top of the salt and block the system.',
      'To check for a salt bridge: press down on the salt with a broom handle. If it\'s hollow underneath, break it up.',
      'Replace the lid.',
    ],
    proTip: 'Running out of salt means your water goes unsoftened — hard water then begins to build up scale in your pipes and appliances. Monthly checks prevent this. Buy salt in bulk to save money.',
    type: 'auto_order',
  },

  water_softener_brine_tank_cleaning: {
    difficulty: 'Moderate', time: '1 hour',
    tools: ['Wet/dry vacuum', 'Scrub brush', 'Mild dish soap', 'Work gloves'],
    steps: [
      'Set the softener to bypass mode.',
      'Scoop out or vacuum out all remaining salt from the brine tank.',
      'Use a wet/dry vacuum to remove any sludge or residue at the bottom.',
      'Fill the tank with water and add a small amount of dish soap.',
      'Scrub the inside walls with a long-handled brush.',
      'Dump the soapy water, rinse thoroughly, and vacuum out the rinse water.',
      'Add fresh salt and restore the softener to service mode.',
      'Manually regenerate the softener by pressing the regeneration button.',
    ],
    proTip: 'Over time, salt sediment and iron deposits accumulate at the bottom of the brine tank, reducing softener efficiency. Annual cleaning keeps the system working properly.',
    type: 'diy',
  },

  water_softener_resin_bed_cleaning: {
    difficulty: 'Moderate', time: '30 minutes',
    tools: ['Resin bed cleaner (Iron Out or equivalent)'],
    steps: [
      'Purchase a resin bed cleaner designed for water softeners.',
      'Pour the recommended amount of cleaner directly into the brine tank on top of the salt.',
      'Manually trigger a regeneration cycle immediately.',
      'The cleaner will flush through the resin bed during the regeneration, removing iron and mineral deposits.',
      'Run water from a tap for a few minutes after regeneration to clear any residual cleaner.',
    ],
    proTip: 'If you have high iron in your well water, clean the resin bed every 6 months instead of every 2 years. Iron fouling is the most common cause of premature resin failure.',
    type: 'diy',
  },

  water_softener_system_settings_check: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Water hardness test strip'],
    steps: [
      'Test your water hardness with a test strip from a hardware store.',
      'Compare the result to your softener\'s current hardness setting — it should match your incoming water hardness.',
      'Check the regeneration schedule — it should be set to regenerate based on water usage or on a time schedule that matches your household.',
      'Check the salt dosage setting — most softeners allow you to adjust how much salt is used per regeneration.',
      'Test the softened water with a test strip — it should show near-zero hardness.',
      'If softened water still tests hard, the resin bed may be exhausted or the brine tank may have a problem.',
    ],
    proTip: 'Water hardness in Northern California varies significantly by source. If you\'ve switched from city to well water or your water source has changed, retest your hardness and update your softener settings.',
    type: 'diy',
  },

  // ── RADIANT HEAT ──────────────────────────────────────────────────────────

  radiant_heat_boiler_inspection_service: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'Boiler service must be performed by a licensed HVAC or plumbing technician. Working on a boiler involves high-temperature/high-pressure systems and in some cases, gas lines.',
    steps: [
      'Schedule annual boiler service before heating season — typically September or October.',
      'Ask the technician to: clean the heat exchanger, check the burner for proper combustion, inspect the flue and venting, test the pressure relief valve, check the expansion tank pressure, and inspect all zone valves and controls.',
      'Ask them to check the boiler water chemistry — the pH should be between 7–8.5 and inhibitors should be at the correct concentration.',
      'Ask for a written service report.',
      'If your boiler is over 20 years old, ask about efficiency ratings for modern replacements.',
    ],
    proTip: 'Annual boiler service costs $100–$200 and keeps your system running at peak efficiency. An unserviced boiler can lose 20–30% efficiency over time and fail at the worst possible moment.',
    type: 'contractor',
  },

  'radiant_heat_bleed_radiators': {
    difficulty: 'Easy', time: '30 minutes',
    tools: ['Radiator bleed key or flat-head screwdriver', 'Small bowl or towel', 'Work gloves'],
    steps: [
      'Turn on your heating system and let it run until the radiators are warm.',
      'Feel each radiator — if the top is cooler than the bottom, there\'s air trapped inside.',
      'Turn off the boiler and let the system cool slightly — about 20 minutes.',
      'Start with the radiator farthest from the boiler on the top floor.',
      'Place a small bowl under the bleed valve — a small valve at the top of the radiator.',
      'Insert the bleed key and slowly turn counterclockwise about a quarter turn.',
      'You\'ll hear hissing air escaping. When water starts to drip steadily instead of air, close the valve.',
      'Move to each radiator in the system, working from top floor to bottom, farthest to nearest.',
      'After bleeding all radiators, check the boiler pressure gauge — it should read between 1–1.5 bar. Add water through the filling loop if pressure is too low.',
    ],
    proTip: 'Bleeding radiators each fall before heating season ensures even heat distribution and maximum efficiency. Air in the system makes your boiler work harder and leaves cold spots in rooms.',
    type: 'diy',
  },

  radiant_heat_check_expansion_tank: {
    difficulty: 'Moderate', time: '20 minutes',
    tools: ['Tire pressure gauge'],
    steps: [
      'Locate the expansion tank — a small tank (usually football-sized) connected to the boiler piping.',
      'Turn off the boiler and let it cool.',
      'Locate the Schrader valve on the tank (like a tire valve).',
      'Check the air pressure with a tire gauge — it should match the boiler\'s cold fill pressure (typically 12–15 psi).',
      'If pressure is too low, add air with a bicycle pump.',
      'If you get water from the Schrader valve instead of air, the bladder inside the tank has ruptured — the tank needs replacement.',
      'A waterlogged expansion tank causes pressure relief valve dripping and boiler pressure spikes.',
    ],
    proTip: 'Expansion tank failure is a common and frequently overlooked boiler problem. A replacement tank costs $40–$80 and is a straightforward DIY install for someone comfortable with basic plumbing.',
    type: 'diy',
  },

  radiant_heat_inspect_zone_valves: {
    difficulty: 'Advanced', time: '1 hour (professional)',
    tools: [],
    contractorNote: 'Zone valve replacement involves draining system water and working near the boiler — best left to a plumber or HVAC technician unless you are experienced.',
    steps: [
      'Turn on each zone individually at the thermostat and verify that heat comes up in that zone.',
      'If a zone doesn\'t heat, the zone valve may be stuck or failed.',
      'Locate the zone valve on the supply or return pipe for that zone — usually near the boiler.',
      'Check the end switch indicator on the valve — most have a small indicator that shows when the valve is open.',
      'If the valve is stuck open, all zones will be warm all the time.',
      'If the valve is stuck closed, that zone won\'t heat.',
      'Zone valve replacement is typically $150–$300 for a technician.',
    ],
    proTip: 'Zone valves last 15–20 years on average. If you have multiple zones failing, it\'s often worth replacing all valves at once since the labor cost is the same.',
    type: 'contractor',
  },

  // ── ELECTRICAL PANEL ──────────────────────────────────────────────────────

  electrical_panel_visual_inspection: {
    difficulty: 'Easy', time: '15 minutes',
    tools: ['Flashlight'],
    warning: 'Never touch any wiring or components inside the electrical panel. This inspection is visual only — open the door and look, do not touch.',
    steps: [
      'Open the panel door — the outer door only, not the inner cover over the breakers.',
      'Look for any signs of: burn marks, scorch marks, or discoloration around any breaker.',
      'Check for any burning smell when the door is open.',
      'Look for any breakers that appear to be in a middle position — this indicates a tripped breaker.',
      'Check that all breakers are labeled and legible.',
      'Look for any moisture, rust, or corrosion inside the panel.',
      'Check that no wires or cables are visibly damaged or burnt.',
      'If you see any burn marks, smell burning, or notice corrosion — call an electrician immediately.',
    ],
    proTip: 'If your panel is a Federal Pacific or Zinsco brand (common in homes built 1950s–1980s), have it evaluated by an electrician — these panels have documented safety issues and are considered fire hazards.',
    type: 'diy',
  },

  'electrical_panel_test_gfci_&_afci_breakers': {
    difficulty: 'Easy', time: '15 minutes',
    tools: [],
    steps: [
      'GFCI (Ground Fault Circuit Interrupter) breakers protect wet areas: bathrooms, kitchens, garages, outdoors.',
      'Locate GFCI outlets (they have "Test" and "Reset" buttons) throughout your home.',
      'Press the "Test" button — the outlet should lose power immediately.',
      'Plug a lamp or phone charger into the outlet and confirm it\'s off.',
      'Press "Reset" to restore power and confirm it works again.',
      'For GFCI breakers in the panel: press the test button on the breaker itself — it should trip. Reset to restore.',
      'AFCI breakers protect against arc faults in bedrooms and living areas. Test them the same way using the test button on the breaker.',
      'If any GFCI or AFCI device fails to trip when tested, it needs to be replaced.',
    ],
    proTip: 'GFCI devices prevent electrocution in wet areas — they\'re required by code for a reason. AFCI breakers prevent electrical fires from arcing faults. Test both every 6 months.',
    type: 'diy',
  },

  electrical_panel_professional_panel_inspection: {
    difficulty: 'Advanced', time: '1–2 hours (professional)',
    tools: [],
    contractorNote: 'All internal panel work must be done by a licensed electrician. Never remove the inner panel cover yourself — live bus bars inside can cause electrocution even with the main breaker off.',
    steps: [
      'Schedule with a licensed electrician every 5 years, or sooner if: breakers trip frequently, lights flicker, outlets feel warm, or the panel is over 25 years old.',
      'The electrician will remove the inner panel cover and inspect all wiring connections for tightness and corrosion.',
      'They will check for double-tapped breakers — two wires on one breaker, which is a safety issue.',
      'They will check the main service entrance wiring for insulation degradation.',
      'They will verify all breakers are functioning properly.',
      'Ask about panel capacity — if you\'re adding an EV charger, solar, or home battery, you may need a larger panel.',
    ],
    proTip: 'Many homeowners don\'t think about their electrical panel until something goes wrong. A panel inspection every 5 years is inexpensive preventive care that can identify serious fire hazards before they become emergencies.',
    type: 'contractor',
  },

  electrical_panel_thermal_imaging_scan: {
    difficulty: 'Advanced', time: '1 hour (professional)',
    tools: [],
    contractorNote: 'Thermal imaging requires a licensed electrician with a thermal camera. The panel must be under load (appliances running) during the scan.',
    steps: [
      'Schedule with a licensed electrician who offers thermal imaging services.',
      'Before they arrive, turn on as many loads as practical — HVAC, dryer, oven — to put the panel under load.',
      'The electrician will carefully remove the inner cover and use a thermal camera to scan all breakers, bus connections, and wiring.',
      'Hot spots indicate: loose connections, overloaded circuits, failing breakers, or corroded connections.',
      'Ask for a thermal imaging report with photos of any hot spots found.',
      'Address any identified hot spots promptly — they are early indicators of potential electrical fires.',
    ],
    proTip: 'Thermal imaging catches problems invisible to the naked eye. Many house fires start inside electrical panels due to loose connections that arc and heat up over time. A thermal scan every 5 years is the best way to catch these before they become disasters.',
    type: 'contractor',
  },
}

// ── KEY ALIASES ────────────────────────────────────────────────────────────
// These map task names that contain & or / (which get stripped differently)
// to their existing guide entries so nothing falls through to "Coming Soon"

guides['pool_pump_&_motor_inspection'] = guides['pool_pump_motor_inspection']
guides['generator_oil_&_filter_change'] = guides['generator_oil_filter_change']
guides['battery_software__firmware_update'] = guides['battery_software_firmware_update']
guides['battery_connection_&_wiring_inspection'] = guides['battery_connection_wiring_inspection']
guides['sump_pump_clean_pump_&_pit'] = guides['sump_pump_clean_pump_pit']
guides['whole_house_fan_belt_&_pulley_inspection'] = guides['whole_house_fan_belt_pulley_inspection']
guides['water_softener_salt_level_check_&_refill'] = guides['water_softener_salt_level_check_refill']
guides['radiant_heat_boiler_inspection_&_service'] = guides['radiant_heat_boiler_inspection_service']

// ── ADU GUIDES ─────────────────────────────────────────────────────────────

guides['adu_general_inspection'] = {
  difficulty: 'Easy', time: '30 minutes',
  tools: ['Flashlight', 'Notepad'],
  steps: [
    'Walk through the ADU and inspect all rooms for signs of water damage, mold, or pest activity.',
    'Check all windows and doors open, close, and lock properly.',
    'Test all light switches, outlets, and ceiling fans.',
    'Run all faucets and check under sinks for leaks.',
    'Flush all toilets and confirm they fill and stop properly.',
    'Check the water heater (if separate) for leaks and proper operation.',
    'Inspect the HVAC filter and replace if dirty.',
    'Test smoke and CO detectors.',
    'Check the electrical panel for any tripped breakers.',
    'Note any needed repairs and address them promptly.',
  ],
  proTip: 'For ADUs used as rentals, conduct this inspection between tenants and document everything with photos. A thorough move-in/move-out inspection protects both landlord and tenant.',
  type: 'diy',
}

guides['adu_smoke_&_co_detector_test'] = {
  difficulty: 'Easy', time: '10 minutes',
  tools: [],
  steps: [
    'Locate all smoke and CO detectors in the ADU — California law requires smoke detectors in each bedroom, outside each sleeping area, and on each floor.',
    'Press and hold the test button on each detector for 3–5 seconds.',
    'The alarm should sound loudly. If it chirps weakly, replace the battery.',
    'Check the manufacturing date on the back of each unit — smoke detectors must be replaced every 10 years.',
    'California also requires CO detectors in all units with attached garages or gas appliances.',
    'If any detector fails the test, replace it immediately — this is a life safety requirement and a landlord legal obligation.',
  ],
  proTip: 'California law requires working smoke alarms in rental units. As a landlord, document your detector tests with dates and photos. Non-compliance can result in significant fines and liability.',
  type: 'diy',
}

guides['adu_hvac_filter_check'] = {
  difficulty: 'Easy', time: '10 minutes',
  tools: ['Replacement filter (check size on existing filter)'],
  steps: [
    'Locate the HVAC filter in the ADU — usually in a wall return vent or inside the air handler.',
    'Remove the filter and hold it up to light — if you cannot see light through it, it needs replacement.',
    'Note the size printed on the filter frame.',
    'Insert a new filter with the airflow arrows pointing toward the air handler.',
    'Write the installation date on the filter frame.',
  ],
  proTip: 'For rental ADUs, include HVAC filter replacement in your tenant welcome packet — provide the correct filter size and remind them to change it every 90 days. A clogged filter is one of the most common causes of HVAC failure.',
  type: 'auto_order',
}

guides['adu_plumbing_inspection'] = {
  difficulty: 'Easy', time: '20 minutes',
  tools: ['Flashlight'],
  steps: [
    'Check under all sinks — kitchen and bathroom — for any signs of leaks, moisture, or water stains.',
    'Run all faucets at full pressure and check that water drains quickly with no gurgling.',
    'Flush all toilets and confirm they fill completely and stop running within 60 seconds.',
    'Check the toilet base for any moisture or soft flooring that could indicate a wax ring leak.',
    'Run the shower and check for adequate pressure and proper drainage.',
    'If the ADU has a separate water heater, check around the base for any moisture or rust.',
    'Look for any visible pipe corrosion under sinks.',
    'Note any issues and address them before a tenant moves in.',
  ],
  proTip: 'Plumbing issues are the most common maintenance request from ADU tenants. A thorough pre-tenancy plumbing check prevents calls in the first month and protects your property from water damage.',
  type: 'contractor',
}

// ── OTHER SYSTEM GUIDES ────────────────────────────────────────────────────

guides['other_cleaning_&_maintenance'] = {
  difficulty: 'Easy', time: 'As needed',
  tools: ['Appropriate cleaning supplies for the system'],
  steps: [
    'Consult your owner\'s manual or manufacturer website for specific cleaning instructions.',
    'Remove any dust, debris, or buildup from accessible surfaces.',
    'Check for any signs of wear, damage, or unusual operation.',
    'Lubricate any moving parts as recommended by the manufacturer.',
    'Test the system\'s operation after cleaning.',
    'Note the date of service and any observations.',
  ],
  proTip: 'Keep a simple maintenance log for every system in your home. Date, what you did, and any issues noticed. This history is invaluable for troubleshooting and adds real value at resale.',
  type: 'diy',
}

// Helper to build a guide key from system/appliance type and task name
export function getGuideKey(type, taskName) {
  const slug = taskName
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_&]/g, '')
  return `${type}_${slug}`
}

function CompleteSection({ onMarkComplete, onClose }) {
  const [showForm, setShowForm] = useState(false)
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [cost, setCost] = useState('')

  const handleSave = () => {
    onMarkComplete({ date, cost: cost ? parseFloat(cost) : null })
    onClose()
  }

  if (!showForm) {
    return (
      <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
        <button
          style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' }}
          onClick={onClose}
        >Close</button>
        <button
          style={{ flex: 2, background: `linear-gradient(135deg, ${C.success}, #3D8A60)`, border: 'none', borderRadius: '10px', padding: '12px', fontSize: '13px', color: '#fff', fontWeight: '700', cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' }}
          onClick={() => setShowForm(true)}
        >✓ Mark as Complete</button>
      </div>
    )
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ background: C.bg, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '16px', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: C.brown, marginBottom: '14px' }}>Log this completion</div>
        <label style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', display: 'block', fontFamily: 'Calibri, sans-serif' }}>Date Completed</label>
        <input
          type="date"
          value={date}
          max={new Date().toISOString().split('T')[0]}
          onChange={e => setDate(e.target.value)}
          style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', marginBottom: '12px' }}
        />
        <label style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', display: 'block', fontFamily: 'Calibri, sans-serif' }}>Cost (optional)</label>
        <input
          type="number"
          placeholder="e.g. 45"
          value={cost}
          onChange={e => setCost(e.target.value)}
          style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', marginBottom: '14px' }}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: 'Georgia, serif' }}
            onClick={() => setShowForm(false)}
          >Cancel</button>
          <button
            style={{ flex: 2, background: `linear-gradient(135deg, ${C.success}, #3D8A60)`, border: 'none', borderRadius: '10px', padding: '11px', fontSize: '13px', color: '#fff', fontWeight: '700', cursor: 'pointer', fontFamily: 'Georgia, serif' }}
            onClick={handleSave}
          >✓ Save & Complete</button>
        </div>
      </div>
    </div>
  )
}

export default function HowToGuide({ task, applianceName, applianceType, onClose, onMarkComplete }) {
  const key = getGuideKey(applianceType, task.task)
  const guide = guides[key]

  if (!guide) {
    return (
      <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div style={S.modal}>
          <div style={S.header}>
            <div>
              <div style={S.taskTitle}>{task.task}</div>
              <div style={S.applianceName}>{applianceName}</div>
            </div>
            <button style={S.closeBtn} onClick={onClose}>×</button>
          </div>
          <div style={S.body}>
            <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '14px', lineHeight: 1.7 }}>
              Detailed instructions for this task are coming soon. In the meantime, refer to your owner's manual or search for "{applianceName} {task.task}" for guidance.
            </div>
            {onMarkComplete
              ? <CompleteSection onMarkComplete={onMarkComplete} onClose={onClose} />
              : <button style={S.secondaryBtn} onClick={onClose}>Close</button>
            }
          </div>
        </div>
      </div>
    )
  }

  const diffColor = difficultyColors[guide.difficulty] || difficultyColors['Moderate']
  const taskTypeColor = typeColors[guide.type] || typeColors.diy

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={{ flex: 1 }}>
            <div style={S.taskTitle}>{task.task}</div>
            <div style={S.applianceName}>{applianceName} · {task.interval}</div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={S.body}>
          <div style={S.metaRow}>
            <div style={S.metaBadge(diffColor)}>
              {guide.difficulty === 'Easy' ? <Check size={12} strokeWidth={2} style={{ marginRight: '3px', verticalAlign: 'middle' }} /> : guide.difficulty === 'Moderate' ? <AlertTriangle size={12} strokeWidth={2} style={{ marginRight: '3px', verticalAlign: 'middle' }} /> : <Zap size={12} strokeWidth={2} style={{ marginRight: '3px', verticalAlign: 'middle' }} />}{guide.difficulty}
            </div>
            <div style={S.metaBadge({ bg: C.bg, text: C.muted })}>
              ⏱ {guide.time}
            </div>
            <div style={S.metaBadge(taskTypeColor)}>
              {typeLabels[guide.type]}
            </div>
          </div>

          {guide.warning && (
            <div style={S.warningBox}>
              <div style={S.warningText}><AlertTriangle size={14} strokeWidth={1.5} color={C.danger} style={{ marginRight: '5px', verticalAlign: 'middle', flexShrink: 0 }} /><strong>Safety note:</strong> {guide.warning}</div>
            </div>
          )}

          {guide.contractorNote && (
            <div style={S.contractorNote}>
              <div style={S.contractorNoteText}>
                <Wrench size={14} strokeWidth={1.5} color={C.success} style={{ marginRight: '5px', verticalAlign: 'middle', flexShrink: 0 }} /><strong>Contractor note:</strong> {guide.contractorNote}
              </div>
            </div>
          )}

          {guide.tools && guide.tools.length > 0 && (
            <>
              <div style={S.sectionTitle}>What You'll Need</div>
              <div style={S.toolsList}>
                {guide.tools.map((tool, i) => (
                  <div key={i} style={S.toolChip}>🔹 {tool}</div>
                ))}
              </div>
              <div style={S.divider} />
            </>
          )}

          <div style={S.sectionTitle}>Step-by-Step Instructions</div>
          {guide.steps.map((step, i) => (
            <div key={i} style={S.stepRow}>
              <div style={S.stepNumber}>{i + 1}</div>
              <div style={S.stepText}>{step}</div>
            </div>
          ))}

          {guide.proTip && (
            <>
              <div style={S.divider} />
              <div style={S.proTip}>
                <div style={S.proTipLabel}>💡 Pro Tip</div>
                <div style={S.proTipText}>{guide.proTip}</div>
              </div>
            </>
          )}

          {onMarkComplete
            ? <CompleteSection onMarkComplete={onMarkComplete} onClose={onClose} />
            : <button style={S.secondaryBtn} onClick={onClose}>Done</button>
          }
        </div>
      </div>
    </div>
  )
}